import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Mail, Lock, User, Globe, Phone, FileText, Check, AlertCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';
import { toast } from 'sonner';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';

const DEFAULT_BLUE = '#2B4B6A';
const DEFAULT_ORANGE = '#F58A5A';

const CompanyRegistration = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Company Info, 2: Admin Account
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [theme, setTheme] = useState(null);
  
  const [formData, setFormData] = useState({
    // Company Info
    company_name: '',
    subdomain: '',
    description: '',
    phone: '',
    website: '',
    // Admin Account
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: ''
  });
  
  const [errors, setErrors] = useState({});

  // Get external page colors from global theme
  const externalPageBg = theme?.admin_theme?.externalPages?.pageBackgroundColor || '#F9FAFB';
  const primaryColor = theme?.admin_theme?.buttons?.primaryColor || DEFAULT_BLUE;
  const accentColor = theme?.admin_theme?.buttons?.secondaryColor || DEFAULT_ORANGE;

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const res = await api.get('/theme');
        setTheme(res.data);
      } catch (error) {
        console.log('Using default theme');
      }
    };
    fetchTheme();
  }, []);

  const checkSubdomain = async (subdomain) => {
    if (!subdomain || subdomain.length < 3) {
      setSubdomainAvailable(null);
      return;
    }
    
    setCheckingSubdomain(true);
    try {
      const response = await api.get(`/companies/check-subdomain/${subdomain.toLowerCase()}`);
      setSubdomainAvailable(response.data.available);
      if (!response.data.available) {
        setErrors(prev => ({ ...prev, subdomain: response.data.reason || 'Subdomain not available' }));
      } else {
        setErrors(prev => ({ ...prev, subdomain: null }));
      }
    } catch (error) {
      setSubdomainAvailable(null);
    } finally {
      setCheckingSubdomain(false);
    }
  };

  const handleSubdomainChange = (value) => {
    // Strip disallowed chars + hard cap at 30 (matches backend validate_subdomain
    // pattern max length + register_company `> 30` check). No silent truncation
    // of shorter values.
    const cleaned = value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 30);
    setFormData(prev => ({ ...prev, subdomain: cleaned }));

    // Debounce availability check
    clearTimeout(window.subdomainTimeout);
    window.subdomainTimeout = setTimeout(() => checkSubdomain(cleaned), 500);
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.company_name.trim()) newErrors.company_name = 'Company name is required';
    if (!formData.subdomain.trim()) newErrors.subdomain = 'Subdomain is required';
    if (formData.subdomain.length < 3) newErrors.subdomain = 'Subdomain must be at least 3 characters';
    if (subdomainAvailable === false) newErrors.subdomain = 'Subdomain is not available';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (formData.password !== formData.confirm_password) newErrors.confirm_password = 'Passwords do not match';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;
    
    setLoading(true);
    try {
      await api.post('/companies/register', {
        company_name: formData.company_name,
        subdomain: formData.subdomain.toLowerCase(),
        description: formData.description || null,
        phone: formData.phone || null,
        website: formData.website || null,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password
      });
      
      toast.success('Registration successful! Please check your email to verify your account.');
      navigate('/login', { state: { message: 'Please verify your email before logging in.' } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: externalPageBg }}>
      <PublicHeader />
      
      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <motion.div 
          className="max-w-lg w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg" style={{ backgroundColor: primaryColor }}>
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Register Your Company</h1>
            <p className="mt-2 text-gray-600">Create your company account on Trados</p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className={`flex items-center gap-2 ${step >= 1 ? '' : 'text-gray-400'}`} style={{ color: step >= 1 ? primaryColor : undefined }}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white`} style={{ backgroundColor: step >= 1 ? primaryColor : '#9CA3AF' }}>
                {step > 1 ? <Check className="w-4 h-4" /> : '1'}
              </div>
              <span className="hidden sm:inline text-sm font-medium">Company Info</span>
            </div>
            <div className={`w-12 h-0.5`} style={{ backgroundColor: step >= 2 ? primaryColor : '#E5E7EB' }} />
            <div className={`flex items-center gap-2 ${step >= 2 ? '' : 'text-gray-400'}`} style={{ color: step >= 2 ? primaryColor : undefined }}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white`} style={{ backgroundColor: step >= 2 ? primaryColor : '#9CA3AF' }}>
                2
              </div>
              <span className="hidden sm:inline text-sm font-medium">Admin Account</span>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Company Info */}
              {step === 1 && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.company_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                        className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 ${errors.company_name ? 'border-red-500' : 'border-gray-200'}`}
                        style={{ '--tw-ring-color': primaryColor }}
                        placeholder="Your Company Name"
                        data-testid="company-name-input"
                      />
                    </div>
                    {errors.company_name && <p className="mt-1 text-sm text-red-500">{errors.company_name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subdomain *</label>
                    <div className="flex items-center">
                      <div className="relative flex-1">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          value={formData.subdomain}
                          onChange={(e) => handleSubdomainChange(e.target.value)}
                          onBlur={(e) => {
                            if (e.target.value !== formData.subdomain) {
                              // browser autocomplete / paste can differ from state
                              handleSubdomainChange(e.target.value);
                            }
                          }}
                          maxLength={30}
                          autoComplete="off"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck={false}
                          className={`w-full pl-10 pr-4 py-3 border rounded-l-xl focus:outline-none focus:ring-2 ${errors.subdomain ? 'border-red-500' : 'border-gray-200'}`}
                          style={{ '--tw-ring-color': primaryColor }}
                          placeholder="yourcompany"
                          data-testid="subdomain-input"
                        />
                      </div>
                      <div className="px-4 py-3 bg-gray-100 border border-l-0 border-gray-200 rounded-r-xl text-gray-500 font-medium">
                        .trados.app
                      </div>
                    </div>
                    <div className="mt-1 flex items-center justify-between">
                      {errors.subdomain && <p className="text-sm text-red-500">{errors.subdomain}</p>}
                      {!errors.subdomain && checkingSubdomain && <p className="text-sm text-gray-500">Checking...</p>}
                      {!errors.subdomain && subdomainAvailable === true && (
                        <p className="text-sm text-green-600 flex items-center gap-1">
                          <Check className="w-4 h-4" /> Available
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': primaryColor }}
                      placeholder="Brief description of your company..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2"
                          style={{ '--tw-ring-color': primaryColor }}
                          placeholder="+1 555-0000"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2"
                        style={{ '--tw-ring-color': primaryColor }}
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleNext}
                    className="w-full flex items-center justify-center gap-2 py-3.5 text-white font-semibold rounded-xl transition shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                    style={{ backgroundColor: primaryColor }}
                    data-testid="company-next-button"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              )}

              {/* Step 2: Admin Account */}
              {step === 2 && (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          value={formData.first_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                          className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 ${errors.first_name ? 'border-red-500' : 'border-gray-200'}`}
                          style={{ '--tw-ring-color': primaryColor }}
                          placeholder="John"
                        />
                      </div>
                      {errors.first_name && <p className="mt-1 text-sm text-red-500">{errors.first_name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 ${errors.last_name ? 'border-red-500' : 'border-gray-200'}`}
                        style={{ '--tw-ring-color': primaryColor }}
                        placeholder="Doe"
                      />
                      {errors.last_name && <p className="mt-1 text-sm text-red-500">{errors.last_name}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 ${errors.email ? 'border-red-500' : 'border-gray-200'}`}
                        style={{ '--tw-ring-color': primaryColor }}
                        placeholder="admin@company.com"
                      />
                    </div>
                    {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 ${errors.password ? 'border-red-500' : 'border-gray-200'}`}
                        style={{ '--tw-ring-color': primaryColor }}
                        placeholder="Min. 8 characters"
                      />
                    </div>
                    {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="password"
                        value={formData.confirm_password}
                        onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
                        className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 ${errors.confirm_password ? 'border-red-500' : 'border-gray-200'}`}
                        style={{ '--tw-ring-color': primaryColor }}
                        placeholder="Re-enter password"
                      />
                    </div>
                    {errors.confirm_password && <p className="mt-1 text-sm text-red-500">{errors.confirm_password}</p>}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium">After registration:</p>
                        <ul className="mt-1 list-disc list-inside text-blue-700">
                          <li>You'll need to verify your email</li>
                          <li>You can explore the admin panel</li>
                          <li>To add users, complete company verification</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1 flex items-center justify-center gap-2 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 flex items-center justify-center gap-2 py-3 text-white font-semibold rounded-xl transition shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50"
                      style={{ backgroundColor: primaryColor }}
                      data-testid="company-submit-button"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          Create Account
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Links */}
          <div className="text-center mt-6 space-y-2">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium hover:underline" style={{ color: primaryColor }}>Sign in</Link>
            </p>
            <p className="text-gray-500 text-sm">
              Looking to join an existing company?{' '}
              <Link to="/register" className="font-medium hover:underline" style={{ color: primaryColor }}>Register as a user</Link>
            </p>
          </div>
        </motion.div>
      </div>
      
      <PublicFooter />
    </div>
  );
};

export default CompanyRegistration;
